<script lang="ts">
  import { Button } from "$lib/components/ui/button";
  import * as Dialog from "$lib/components/ui/dialog";
  import { SubscriptionAPI } from "$lib/api/subscription";
  import { authStore } from "$lib/stores";
  import { cn } from "$lib/utility";
  import { trackUpgradeConversion } from "$lib/utility/adTracking";
  import { PLANS } from "$lib/data/pricing";
  import { ReferralsAPI } from "$lib/api/referrals";

  let { open = $bindable(false), toTierLabel = $bindable("") } = $props();

  let disabled = $state(false);
  let error = $state<string | null>(null);

  $effect(() => {
    if (open) {
      disabled = false;
      error = null;
    }
  });

  function getPlanPrice(planLabel: string): number {
    if (!planLabel) return 0;

    if (planLabel === "free_monthly_tier_1") return 0;

    let planType: "FREE" | "PLUS" | "TEAM" = "FREE";

    if (planLabel.startsWith("plus_tier")) {
      planType = "PLUS";
    } else if (planLabel.startsWith("team_tier")) {
      planType = "TEAM";
    }

    const plan = PLANS[planType];
    if ("subtiers" in plan) {
      const matchingTier = plan.subtiers.find((tier) => tier.label === planLabel);
      return matchingTier?.price || 0;
    }
    return plan.price || 0;
  }

  async function handleSubscriptionChange() {
    try {
      disabled = true;
      ReferralsAPI.ensureReferralCode();
      const result = await SubscriptionAPI.updatePlan($authStore.user?.planLabel ?? "", toTierLabel);

      if (result) {
        const price = getPlanPrice(toTierLabel);

        trackUpgradeConversion(toTierLabel, price);

        await authStore.updateUserPlan();
        open = false;
        window.location.reload();
      }
    } catch (err) {
      error = err instanceof Error ? err.message : "Failed to update subscription";
      disabled = false;
    }
  }
</script>

<Dialog.Root bind:open>
  <Dialog.Portal>
    <Dialog.Overlay class="fixed inset-0 bg-black/50 dark:bg-black/70" />
    <Dialog.Content
      class={cn(
        "fixed left-1/2 top-1/2 w-full max-w-md -translate-x-1/2 -translate-y-1/2",
        "rounded-lg p-6 shadow-lg",
        "bg-white dark:bg-theme-background-tertiary",
        "text-black dark:text-theme-gray-800",
        "border border-black/5 dark:border-white/10",
      )}
    >
      <Dialog.Header>
        <Dialog.Title class="text-lg font-semibold dark:text-theme-gray-800">Subscription change</Dialog.Title>
        <Dialog.Description class="mt-2 text-sm text-gray-600 dark:text-theme-gray-500">
          Please confirm that you want to change the subscription. You will be billed for the new period.
        </Dialog.Description>
      </Dialog.Header>

      {#if error}
        <p class="mt-2 text-sm text-red-600 dark:text-red-400">{error}</p>
      {/if}

      <Dialog.Footer class="flex w-full justify-around gap-2">
        <Button
          onclick={handleSubscriptionChange}
          class={cn(
            "w-full",
            "bg-black text-white",
            "dark:bg-theme-background-primary dark:text-theme-gray-600",
            "dark:border dark:border-white/10",
            "dark:hover:border-white/20 dark:hover:text-theme-gray-800",
            disabled ? "cursor-not-allowed opacity-50" : "",
          )}
          {disabled}
        >
          {#if disabled}
            Processing...
          {:else}
            Update subscription
          {/if}
        </Button>
        <Dialog.Close>
          <Button
            onclick={() => (open = false)}
            variant="outline"
            class={cn(
              "w-full",
              "dark:bg-theme-background-primary dark:text-theme-gray-600",
              "dark:border-white/10",
              "dark:hover:border-white/20 dark:hover:text-theme-gray-800",
            )}
          >
            Cancel
          </Button>
        </Dialog.Close>
      </Dialog.Footer>
    </Dialog.Content>
  </Dialog.Portal>
</Dialog.Root>
